import { useMemo } from "react";
import { useQuery, WatchQueryFetchPolicy } from "@apollo/client";
import { FilterContext } from "@/types/FilterContext";
import { StudentOrdering } from "@/types/StudentOrdering";
import { useOrg } from "@/hooks/useOrg";
import { processStudent } from "@/util/studentUtils";
import { PaginationVariables, PaginatedResults } from "@/util/pagination";
import { Student } from "@/types/Student";
import {
  ActionDetailsStudentListQuery,
  StudentListRiseActionDetailsSortableFieldEnum,
  SortDirectionEnum,
} from "@/__generated__/types";
import { ACTION_DETAILS_STUDENT_LIST } from "@/api/queries/actionDetailsStudentList";

export function useActionStudents({
  paginationVariables,
  ordering,
  skip = false,
  actionId,
  fetchPolicy,
}: {
  paginationVariables?: PaginationVariables | undefined;
  ordering?: StudentOrdering;
  skip?: boolean;
  actionId?: string;
  fetchPolicy?: WatchQueryFetchPolicy;
}): PaginatedResults<Student> {
  const { org } = useOrg();

  const argument = useMemo(() => {
    if (!actionId) return {};

    const context = new FilterContext("rise.actionDetails", {
      riseUserActionId: actionId,
    });
    const defaultOrdering = new StudentOrdering(
      StudentListRiseActionDetailsSortableFieldEnum.Name,
      SortDirectionEnum.Desc,
      "rise.actionDetails",
    );

    return (
      context.buildQueryArgument(
        undefined,
        ordering?.getOrderingQueryInput() ||
          defaultOrdering.getOrderingQueryInput() ||
          undefined,
      ) || {}
    );
  }, [ordering, actionId]);

  const { data, loading, error } = useQuery<ActionDetailsStudentListQuery>(
    ACTION_DETAILS_STUDENT_LIST,
    {
      variables: {
        ...paginationVariables,
        orgId: org?.id || "",
        argument: argument,
        actionId: actionId,
      },
      skip: skip || !org?.id || !argument || !actionId,
      notifyOnNetworkStatusChange: true,
      fetchPolicy,
    },
  );

  return useMemo(() => {
    const studentsResult: PaginatedResults<Student> = {
      items: [],
      hasNextPage: false,
      hasPreviousPage: false,
      totalCount: null,
      loading,
      error,
    };

    const node = data?.node;
    if (node?.__typename === "Organization") {
      const students =
        node.studentList.edges
          ?.map((e) => {
            return e?.node ? processStudent(e?.node) : false;
          })
          .filter((student) => !!student) || [];

      const pageInfo = node.studentList.pageInfo;
      return {
        ...studentsResult,
        ...pageInfo,
        startCursor: pageInfo.startCursor || undefined,
        endCursor: pageInfo.endCursor || undefined,
        totalCount: node.studentList.totalCount,
        items: students,
      };
    }

    return studentsResult;
  }, [data, error, loading]);
}
